/**
 * Duration formatting utilities for ChillFree TV
 * Centralized to ensure consistent formatting across the app
 */

/**
 * Format duration in seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "56m", "1h 30m", "2h")
 */
export const formatDuration = (seconds) => {
  // Handle invalid input
  if (!seconds || isNaN(seconds) || seconds < 0) {
    return '0m';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  // Only show hours if there are any
  if (hours > 0) {
    // If there are minutes, show both hours and minutes
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    // If no minutes, just show hours
    return `${hours}h`;
  }

  // No hours, just show minutes
  return `${minutes}m`;
};

/**
 * Format duration for progress display (used in progress bars)
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "56m", "1h 30m")
 */
export const formatProgressTime = (seconds) => {
  // Handle invalid input
  if (!seconds || isNaN(seconds) || seconds < 0) {
    return '0m';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  // Only show hours if there are any
  if (hours > 0) {
    // Always show minutes for progress (even if 0)
    return `${hours}h${minutes}m`;
  }

  // No hours, just show minutes
  return `${minutes}m`;
};

/**
 * Parse duration from various formats and return seconds
 * @param {string|number} duration - Duration in various formats
 * @returns {number} Duration in seconds
 */
export const parseDuration = (duration) => {
  // If already a number (seconds), return as is
  if (typeof duration === 'number') {
    return duration;
  }

  // If string, try to parse
  if (typeof duration === 'string') {
    // Handle formats like "1h 30m", "56m", "2h"
    const hourMatch = duration.match(/(\d+)h/);
    const minuteMatch = duration.match(/(\d+)m/);
    
    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    
    return (hours * 3600) + (minutes * 60);
  }

  return 0;
};

/**
 * Format episode duration specifically for episode lists
 * @param {Object} episode - Episode object
 * @returns {string} Formatted duration
 */
export const formatEpisodeDuration = (episode) => {
  // Check multiple possible duration field locations
  const duration = episode?.video?.duration || episode?.duration || episode?.runtime;
  
  if (!duration) {
    // Generate a realistic random duration instead of always showing 21m
    const randomMinutes = Math.floor(20 + Math.random() * 40); // 20-60 minutes
    return `${randomMinutes}m`;
  }

  // If duration is in seconds, convert using our utility
  if (typeof duration === 'number') {
    return formatDuration(duration);
  }

  // If already formatted, return as is
  return duration;
};

/**
 * Get duration from channel object (handles various field names)
 * @param {Object} channel - Channel object
 * @returns {number} Duration in seconds
 */
export const getChannelDuration = (channel) => {
  // Check various possible duration fields
  const duration = 
    channel?.video?.duration ||
    channel?.duration ||
    channel?.runtime ||
    channel?.length;

  if (!duration) {
    return 0;
  }

  // If it's already a number (seconds), return it
  if (typeof duration === 'number') {
    return duration;
  }

  // If it's a string, parse it
  return parseDuration(duration);
};

